import { getStoredSessionToken } from '../../../app/services/session.service';
import { buildStaffMemberPayload, validateStaffForm } from '../utils';

const STAFF_API_BASE_URL = (process.env.REACT_APP_STAFF_API_BASE_URL || '').replace(/\/$/, '');
const STAFF_API_PATH = '/api/staff';
const TENANT_DB_ERROR_CODES = new Set([
  'tenant_database_not_ready',
  'tenant_database_unavailable',
  'tenant_database_metadata_missing',
]);
const SESSION_REFRESH_REQUIRED_ERROR_CODES = new Set(['stale_session', 'session_refresh_required']);
const SESSION_EXPIRED_ERROR_CODES = new Set(['session_expired']);

function buildMutationResult({ ok, data = null, error = null, message = '' }) {
  return {
    ok,
    data,
    error,
    message,
  };
}

function getStaffApiUrl(path = '') {
  return `${STAFF_API_BASE_URL}${path || STAFF_API_PATH}`;
}

function getStaffAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

function isAuthFailure(status) {
  return status === 401 || status === 403;
}

async function readErrorPayload(response) {
  try {
    return await response.clone().json();
  } catch {
    return null;
  }
}

function mapTenantDatabaseError(payload) {
  if (!TENANT_DB_ERROR_CODES.has(payload?.error)) {
    return null;
  }

  return {
    error:
      payload.error === 'tenant_database_not_ready'
        ? 'STAFF_TENANT_DATABASE_NOT_READY'
        : payload.error === 'tenant_database_metadata_missing'
          ? 'STAFF_TENANT_DATABASE_METADATA_MISSING'
          : 'STAFF_TENANT_DATABASE_UNAVAILABLE',
    message: payload?.message || 'Database tenant non disponibile per il modulo staff.',
  };
}

function mapCapabilityDeniedError(payload) {
  if (payload?.error !== 'capability_denied') {
    return null;
  }

  return {
    error: 'STAFF_CAPABILITY_DENIED',
    message: payload?.message || 'Non hai i permessi per modificare lo stato del personale.',
  };
}

function mapStaffAuthError(status, payload, fallbackMessage) {
  if (payload?.error === 'module_disabled') {
    return {
      error: 'STAFF_MODULE_DISABLED',
      message: 'Modulo non abilitato per questo workspace.',
    };
  }

  if (SESSION_REFRESH_REQUIRED_ERROR_CODES.has(payload?.error)) {
    return {
      error: 'STAFF_SESSION_REFRESH_REQUIRED',
      message: "Sessione non aggiornata. Effettua di nuovo l'accesso.",
    };
  }

  if (SESSION_EXPIRED_ERROR_CODES.has(payload?.error)) {
    return {
      error: 'STAFF_SESSION_EXPIRED',
      message: 'Sessione scaduta.',
    };
  }

  if (status === 403) {
    return {
      error: 'STAFF_PERMISSION_DENIED',
      message: payload?.message || 'Permessi insufficienti.',
    };
  }

  if (status === 401) {
    return {
      error: 'STAFF_AUTH_REQUIRED',
      message: payload?.message || fallbackMessage,
    };
  }

  return null;
}

function mapApiStaffToStaffMember(member) {
  return {
    id: member?.id,
    name: member?.name || '',
    role: member?.role || '',
    email: member?.email || '',
    phone: member?.phone || '',
    tipoPersonale: member?.tipoPersonale || 'interno',
    status: member?.status || 'active',
    linkedWorkspaceUserId: member?.linkedWorkspaceUserId ?? null,
    ...(member?.accountActive !== undefined ? { accountActive: member.accountActive } : {}),
    ...(member?.workspaceUserActive !== undefined ? { workspaceUserActive: member.workspaceUserActive } : {}),
    skills: Array.isArray(member?.skills) ? member.skills : [],
    activeAssignments: [],
    completedAssignments: [],
    ...(member?.customFields ? { customFields: member.customFields } : {}),
  };
}

export function mapStaffMemberToProfilePayload(profile = {}) {
  return {
    name: profile.name || profile.fullName || '',
    role: profile.role || profile.professionalTitle || profile.jobTitle || '',
    email: profile.email || 'profile@readonly.local',
    phone: profile.phone || '',
    tipoPersonale: profile.tipoPersonale || profile.staffType || 'interno',
    skills: Array.isArray(profile.skills)
      ? profile.skills
      : String(profile.skills || '')
        .split(',')
        .map((skill) => skill.trim())
        .filter(Boolean),
  };
}

async function runStaffLifecycleMutation(staffId, action, successMessage) {
  if (!staffId) {
    return buildMutationResult({
      ok: false,
      error: 'STAFF_NOT_FOUND',
      message: 'Membro del personale non trovato.',
    });
  }

  try {
    const response = await fetch(getStaffApiUrl(`${STAFF_API_PATH}/${staffId}/${action}`), {
      method: 'POST',
      headers: getStaffAuthHeaders(),
    });

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    const capabilityDeniedError = mapCapabilityDeniedError(tenantDbPayload);
    if (capabilityDeniedError) {
      return buildMutationResult({
        ok: false,
        error: capabilityDeniedError.error,
        message: capabilityDeniedError.message,
      });
    }

    if (isAuthFailure(response.status)) {
      const authError = mapStaffAuthError(
        response.status,
        tenantDbPayload,
        'Sessione non autorizzata per il backend staff.'
      );
      return buildMutationResult({
        ok: false,
        error: authError?.error || 'STAFF_AUTH_REQUIRED',
        message: authError?.message || 'Sessione non autorizzata per il backend staff.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'STAFF_NOT_FOUND',
        message: 'Membro del personale non trovato.',
      });
    }

    if (response.status === 409) {
      return buildMutationResult({
        ok: false,
        error: 'STAFF_LIFECYCLE_CONFLICT',
        message: 'Transizione staff non consentita nello stato corrente.',
      });
    }

    if (!response.ok) {
      throw new Error(`STAFF_${action.toUpperCase()}_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        staffMember: mapApiStaffToStaffMember(payload),
      },
      message: successMessage,
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'STAFF_API_UNAVAILABLE',
      message: 'Backend staff non disponibile. Operazione non completata.',
    });
  }
}

function normalizeComparableId(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value);
}

function isAssignmentLinkedToStaff(assignment, staffMember) {
  const normalizedWorkspaceUserId = normalizeComparableId(staffMember?.linkedWorkspaceUserId);
  const normalizedStaffId = normalizeComparableId(staffMember?.id);
  const workspaceCandidateIds = [normalizedWorkspaceUserId].filter(Boolean);
  const staffCandidateIds = [normalizedStaffId].filter(Boolean);
  if (!workspaceCandidateIds.length && !staffCandidateIds.length) return false;

  const recipientIds = Array.isArray(assignment?.recipientIds) ? assignment.recipientIds : [];
  const staffIds = Array.isArray(assignment?.staffIds) ? assignment.staffIds : [];
  const assigneeIds = Array.isArray(assignment?.assigneeIds) ? assignment.assigneeIds : [];
  const directAssigneeIds = [
    assignment?.assigneeId,
    assignment?.assignedTo,
    assignment?.staffId,
    assignment?.linkedWorkspaceUserId,
    assignment?.workspaceUserId,
  ].filter((value) => value !== null && value !== undefined && value !== '');

  return (
    recipientIds.some((recipientId) => workspaceCandidateIds.includes(normalizeComparableId(recipientId))) ||
    staffIds.some((staffId) => staffCandidateIds.includes(normalizeComparableId(staffId))) ||
    assigneeIds.some((assigneeId) => workspaceCandidateIds.includes(normalizeComparableId(assigneeId))) ||
    directAssigneeIds.some((assigneeId) => workspaceCandidateIds.includes(normalizeComparableId(assigneeId)))
  );
}

function mapAssignmentSummary(assignment) {
  return {
    id: assignment?.id,
    description: assignment?.description || 'Task senza descrizione',
    type: assignment?.type || 'altro',
    urgency: assignment?.urgency || 'media',
    dueDate: assignment?.dueDate || '',
    status: assignment?.status || 'da_accettare',
    senderId: assignment?.senderId ?? null,
    recipientIds: Array.isArray(assignment?.recipientIds) ? [...assignment.recipientIds] : [],
  };
}

export function buildStaffOperationalReadModel(staffList = [], assignments = []) {
  const safeAssignments = Array.isArray(assignments) ? assignments : [];

  return (Array.isArray(staffList) ? staffList : []).map((staffMember) => {
    const linkedAssignments = safeAssignments.filter((assignment) =>
      isAssignmentLinkedToStaff(assignment, staffMember)
    );

    return {
      ...staffMember,
      activeAssignments: linkedAssignments
        .filter((assignment) => assignment.status !== 'completato' && assignment.status !== 'chiuso')
        .map(mapAssignmentSummary),
      completedAssignments: linkedAssignments
        .filter((assignment) => assignment.status === 'completato' || assignment.status === 'chiuso')
        .map(mapAssignmentSummary),
    };
  });
}

function buildStaffRequest(form) {
  const payload = buildStaffMemberPayload(form, null);

  return {
    name: payload.name,
    role: payload.role,
    email: payload.email,
    phone: payload.phone,
    tipoPersonale: payload.tipoPersonale,
    skills: payload.skills,
    ...(Object.prototype.hasOwnProperty.call(form, 'customFields')
      ? { customFields: form.customFields }
      : {}),
  };
}

export async function listStaffCustomFields(){const response=await fetch(getStaffApiUrl(`${STAFF_API_PATH}/custom-fields`),{headers:getStaffAuthHeaders()});const payload=await readErrorPayload(response);if(!response.ok)throw new Error(payload?.message||'Campi personalizzati non disponibili.');return Array.isArray(payload)?payload:[];}
export async function createStaffCustomField(definition){const response=await fetch(getStaffApiUrl(`${STAFF_API_PATH}/custom-fields`),{method:'POST',headers:getStaffAuthHeaders({'Content-Type':'application/json'}),body:JSON.stringify(definition)});const payload=await response.json().catch(()=>null);return response.ok?buildMutationResult({ok:true,data:payload}):buildMutationResult({ok:false,error:'CUSTOM_FIELD_CREATE_FAILED',message:payload?.message||'Impossibile salvare il campo.'});}
export async function archiveStaffCustomField(id){const response=await fetch(getStaffApiUrl(`${STAFF_API_PATH}/custom-fields/${id}`),{method:'DELETE',headers:getStaffAuthHeaders()});const payload=await readErrorPayload(response);return response.ok?buildMutationResult({ok:true}):buildMutationResult({ok:false,error:'CUSTOM_FIELD_ARCHIVE_FAILED',message:payload?.message||'Impossibile archiviare il campo.'});}

// Real staff catalog source of truth for backend-driven read flows.
export async function listStaff({ includeInactive = false } = {}) {
  const path = includeInactive ? `${STAFF_API_PATH}?includeInactive=true` : STAFF_API_PATH;
  const response = await fetch(getStaffApiUrl(path), {
    headers: getStaffAuthHeaders(),
  });

  const tenantDbPayload = await readErrorPayload(response);
  const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
  if (tenantDbError) {
    const error = new Error(tenantDbError.error);
    error.userMessage = tenantDbError.message;
    throw error;
  }

  if (!response.ok) {
    if (isAuthFailure(response.status)) {
      const authError = mapStaffAuthError(
        response.status,
        tenantDbPayload,
        'Sessione non autorizzata per il catalogo staff.'
      );
      const error = new Error(authError?.error || `STAFF_AUTH_REQUIRED_${response.status}`);
      error.userMessage = authError?.message || 'Sessione non autorizzata per il catalogo staff.';
      throw error;
    }

    throw new Error(`STAFF_LIST_FAILED_${response.status}`);
  }

  const payload = await response.json();
  if (!Array.isArray(payload)) {
    throw new Error('STAFF_LIST_INVALID_PAYLOAD');
  }

  return payload.map(mapApiStaffToStaffMember);
}

export async function listStaffResult(options) {
  try {
    const staff = await listStaff(options);

    return buildMutationResult({
      ok: true,
      data: staff,
      error: null,
      message: '',
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'STAFF_API_UNAVAILABLE';
    const isAuthRequired =
      /STAFF_AUTH_REQUIRED_(401|403)$/.test(errorMessage) ||
      errorMessage === 'STAFF_AUTH_REQUIRED' ||
      errorMessage === 'STAFF_SESSION_REFRESH_REQUIRED' ||
      errorMessage === 'STAFF_SESSION_EXPIRED' ||
      errorMessage === 'STAFF_PERMISSION_DENIED' ||
      errorMessage === 'STAFF_MODULE_DISABLED';
    const tenantDbError =
      errorMessage === 'STAFF_TENANT_DATABASE_NOT_READY' ||
      errorMessage === 'STAFF_TENANT_DATABASE_METADATA_MISSING' ||
      errorMessage === 'STAFF_TENANT_DATABASE_UNAVAILABLE';

    return buildMutationResult({
      ok: false,
      data: [],
      error: tenantDbError ? errorMessage : isAuthRequired ? errorMessage : 'STAFF_API_UNAVAILABLE',
      message: isAuthRequired
        ? error instanceof Error && typeof error.userMessage === 'string'
          ? error.userMessage
          : 'Sessione non autorizzata per il catalogo staff.'
        : tenantDbError && error instanceof Error && typeof error.userMessage === 'string'
          ? error.userMessage
        : 'Catalogo staff non disponibile dal backend.',
    });
  }
}

export async function getOwnStaffProfile() {
  try {
    const response = await fetch(getStaffApiUrl(`${STAFF_API_PATH}/me/profile`), {
      headers: getStaffAuthHeaders(),
    });
    const payload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(payload);
    if (tenantDbError) {
      return buildMutationResult({ ok: false, error: tenantDbError.error, message: tenantDbError.message });
    }
    if (isAuthFailure(response.status)) {
      const authError = mapStaffAuthError(response.status, payload, 'Sessione non autorizzata per il profilo staff.');
      return buildMutationResult({ ok: false, error: authError?.error || 'STAFF_AUTH_REQUIRED', message: authError?.message || 'Sessione non autorizzata.' });
    }
    if (response.status === 404) {
      return buildMutationResult({ ok: false, error: 'STAFF_PROFILE_MISSING', message: 'Profilo staff da completare.' });
    }
    if (!response.ok) {
      throw new Error(`STAFF_PROFILE_GET_FAILED_${response.status}`);
    }
    return buildMutationResult({ ok: true, data: { profile: mapApiStaffToStaffMember(payload) } });
  } catch {
    return buildMutationResult({ ok: false, error: 'STAFF_API_UNAVAILABLE', message: 'Profilo staff non disponibile.' });
  }
}

export async function upsertOwnStaffProfile(profile) {
  try {
    const response = await fetch(getStaffApiUrl(`${STAFF_API_PATH}/me/profile`), {
      method: 'PUT',
      headers: getStaffAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(mapStaffMemberToProfilePayload(profile)),
    });
    const payload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(payload);
    if (tenantDbError) {
      return buildMutationResult({ ok: false, error: tenantDbError.error, message: tenantDbError.message });
    }
    if (isAuthFailure(response.status)) {
      const authError = mapStaffAuthError(response.status, payload, 'Sessione non autorizzata per salvare il profilo staff.');
      return buildMutationResult({ ok: false, error: authError?.error || 'STAFF_AUTH_REQUIRED', message: authError?.message || 'Sessione non autorizzata.' });
    }
    if (response.status === 400) {
      return buildMutationResult({ ok: false, error: 'STAFF_PROFILE_VALIDATION_ERROR', message: 'Completa telefono, ruolo professionale e tipo personale.' });
    }
    if (!response.ok) {
      throw new Error(`STAFF_PROFILE_UPSERT_FAILED_${response.status}`);
    }
    return buildMutationResult({
      ok: true,
      data: { profile: mapApiStaffToStaffMember(payload) },
      message: 'Profilo operativo aggiornato.',
    });
  } catch {
    return buildMutationResult({ ok: false, error: 'STAFF_API_UNAVAILABLE', message: 'Salvataggio profilo staff non completato.' });
  }
}

export async function archiveStaffByWorkspaceUser(workspaceUserId) {
  if (!workspaceUserId) {
    return buildMutationResult({ ok: false, error: 'STAFF_WORKSPACE_USER_REQUIRED', message: 'Utente workspace non valido.' });
  }
  try {
    const response = await fetch(getStaffApiUrl(`${STAFF_API_PATH}/workspace-users/${workspaceUserId}/archive`), {
      method: 'POST',
      headers: getStaffAuthHeaders(),
    });
    const payload = await readErrorPayload(response);
    if (isAuthFailure(response.status)) {
      const authError = mapStaffAuthError(response.status, payload, 'Sessione non autorizzata per archiviare il profilo staff.');
      return buildMutationResult({ ok: false, error: authError?.error || 'STAFF_AUTH_REQUIRED', message: authError?.message || 'Sessione non autorizzata.' });
    }
    if (!response.ok) {
      throw new Error(`STAFF_ARCHIVE_BY_USER_FAILED_${response.status}`);
    }
    return buildMutationResult({ ok: true, message: 'Profilo staff archiviato.' });
  } catch {
    return buildMutationResult({ ok: false, error: 'STAFF_API_UNAVAILABLE', message: 'Archiviazione profilo staff non completata.' });
  }
}

export async function createStaffMember(form) {
  try {
    const response = await fetch(getStaffApiUrl(STAFF_API_PATH), {
      method: 'POST',
      headers: getStaffAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(buildStaffRequest(form)),
    });

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    const capabilityDeniedError = mapCapabilityDeniedError(tenantDbPayload);
    if (capabilityDeniedError) {
      return buildMutationResult({
        ok: false,
        error: capabilityDeniedError.error,
        message: capabilityDeniedError.message,
      });
    }

    if (isAuthFailure(response.status)) {
      const authError = mapStaffAuthError(
        response.status,
        tenantDbPayload,
        'Sessione non autorizzata per il backend staff.'
      );
      return buildMutationResult({
        ok: false,
        error: authError?.error || 'STAFF_AUTH_REQUIRED',
        message: authError?.message || 'Sessione non autorizzata per il backend staff.',
      });
    }

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'STAFF_VALIDATION_ERROR',
        message: 'Compila tutti i campi obbligatori',
      });
    }

    if (!response.ok) {
      throw new Error(`STAFF_CREATE_FAILED_${response.status}`);
    }

    const payload = await response.json();

    return buildMutationResult({
      ok: true,
      data: {
        staffMember: mapApiStaffToStaffMember(payload),
      },
      message: 'Membro del personale aggiunto con successo!',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'STAFF_API_UNAVAILABLE',
      message: 'Backend staff non disponibile. Creazione personale non completata.',
    });
  }
}

export async function updateStaffMember(staffMember) {
  if (!staffMember?.id) {
    return buildMutationResult({
      ok: false,
      error: 'STAFF_NOT_FOUND',
      message: 'Membro del personale non trovato.',
    });
  }

  try {
    const response = await fetch(getStaffApiUrl(`${STAFF_API_PATH}/${staffMember.id}`), {
      method: 'PUT',
      headers: getStaffAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(buildStaffRequest(staffMember)),
    });

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (isAuthFailure(response.status)) {
      const authError = mapStaffAuthError(
        response.status,
        tenantDbPayload,
        'Sessione non autorizzata per il backend staff.'
      );
      return buildMutationResult({
        ok: false,
        error: authError?.error || 'STAFF_AUTH_REQUIRED',
        message: authError?.message || 'Sessione non autorizzata per il backend staff.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'STAFF_NOT_FOUND',
        message: 'Membro del personale non trovato.',
      });
    }

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'STAFF_VALIDATION_ERROR',
        message: 'Compila tutti i campi obbligatori',
      });
    }

    if (!response.ok) {
      throw new Error(`STAFF_UPDATE_FAILED_${response.status}`);
    }

    const payload = await response.json();

    return buildMutationResult({
      ok: true,
      data: {
        staffMember: mapApiStaffToStaffMember(payload),
      },
      message: 'Membro del personale aggiornato con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'STAFF_API_UNAVAILABLE',
      message: 'Backend staff non disponibile. Aggiornamento personale non completato.',
    });
  }
}

export async function deactivateStaffMember(staffId) {
  return runStaffLifecycleMutation(staffId, 'deactivate', 'Membro del personale disattivato con successo.');
}

export async function archiveStaffMember(staffId) {
  return runStaffLifecycleMutation(staffId, 'archive', 'Membro del personale archiviato con successo.');
}

export async function reactivateStaffMember(staffId) {
  return runStaffLifecycleMutation(staffId, 'reactivate', 'Membro del personale riattivato con successo.');
}

export async function deleteStaffMemberPermanently(staffId) {
  try {
    const response = await fetch(getStaffApiUrl(`${STAFF_API_PATH}/${staffId}`), {
      method: 'DELETE',
      headers: getStaffAuthHeaders(),
    });
    if (response.status === 409) return buildMutationResult({ ok: false, error: 'STAFF_DELETE_CONFLICT', message: 'Il membro è collegato ad altri dati e può restare disattivato.' });
    if (!response.ok) return buildMutationResult({ ok: false, error: `STAFF_DELETE_FAILED_${response.status}`, message: 'Eliminazione definitiva non consentita.' });
    return buildMutationResult({ ok: true, message: 'Membro del personale eliminato definitivamente.' });
  } catch {
    return buildMutationResult({ ok: false, error: 'STAFF_API_UNAVAILABLE', message: 'Backend staff non disponibile.' });
  }
}

export async function linkStaffWorkspaceUser(staffId, linkedWorkspaceUserId) {
  if (!staffId) {
    return buildMutationResult({
      ok: false,
      error: 'STAFF_NOT_FOUND',
      message: 'Membro del personale non trovato.',
    });
  }

  try {
    const response = await fetch(getStaffApiUrl(`${STAFF_API_PATH}/${staffId}/workspace-user`), {
      method: 'PATCH',
      headers: getStaffAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ linkedWorkspaceUserId: linkedWorkspaceUserId || null }),
    });

    const payload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(payload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    const capabilityDeniedError = mapCapabilityDeniedError(payload);
    if (capabilityDeniedError) {
      return buildMutationResult({
        ok: false,
        error: capabilityDeniedError.error,
        message: capabilityDeniedError.message,
      });
    }

    if (isAuthFailure(response.status)) {
      const authError = mapStaffAuthError(response.status, payload, 'Sessione non autorizzata per il backend staff.');
      return buildMutationResult({
        ok: false,
        error: authError?.error || 'STAFF_AUTH_REQUIRED',
        message: authError?.message || 'Sessione non autorizzata per il backend staff.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'STAFF_WORKSPACE_USER_LINK_NOT_FOUND',
        message: 'Personale o utente workspace non trovato.',
      });
    }

    if (response.status === 409) {
      return buildMutationResult({
        ok: false,
        error: 'STAFF_WORKSPACE_USER_ALREADY_LINKED',
        message: 'Utente workspace gia collegato a un altro membro del personale.',
      });
    }

    if (!response.ok) {
      throw new Error(`STAFF_WORKSPACE_USER_LINK_FAILED_${response.status}`);
    }

    return buildMutationResult({
      ok: true,
      data: {
        staffMember: mapApiStaffToStaffMember(payload),
      },
      message: linkedWorkspaceUserId ? 'Utente workspace collegato.' : 'Utente workspace scollegato.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'STAFF_API_UNAVAILABLE',
      message: 'Backend staff non disponibile. Collegamento utente non completato.',
    });
  }
}

export function createStaff(staffList, form, nextId) {
  const validationError = validateStaffForm(form);
  if (validationError) {
    return buildMutationResult({
      ok: false,
      error: 'STAFF_VALIDATION_ERROR',
      message: validationError,
    });
  }

  const payload = buildStaffMemberPayload(form, nextId);
  const staffMember = createStaffMember(payload);

  return buildMutationResult({
    ok: true,
    data: {
      staffList: [...staffList, staffMember],
      staffMember,
    },
      message: 'Membro del personale aggiunto con successo!',
    });
}

async function staffNotesRequest(staffId, suffix = '', method = 'GET', body) {
  try {
    const response = await fetch(getStaffApiUrl(`${STAFF_API_PATH}/${staffId}/notes${suffix}`), {
      method, headers: getStaffAuthHeaders({ 'Content-Type': 'application/json' }),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) {
      const payload = await readErrorPayload(response);
      const detail = mapTenantDatabaseError(payload) || mapStaffAuthError(response.status, payload, 'Sessione non autorizzata.') || {};
      return buildMutationResult({ ok: false, message: detail.message || 'Operazione sui commenti non completata.' });
    }
    return buildMutationResult({ ok: true, data: response.status === 204 ? null : await response.json() });
  } catch { return buildMutationResult({ ok: false, message: 'Commenti del personale non disponibili. Riprova.' }); }
}
export const listStaffNotesResult = staffId => staffNotesRequest(staffId);
export const createStaffNoteResult = (staffId, body, parentId = null) => staffNotesRequest(staffId, '', 'POST', { body, parentId });
export const deleteStaffNoteResult = (staffId, noteId) => staffNotesRequest(staffId, `/${noteId}`, 'DELETE');
